<script>
import General from "@/services/General";

const DEFAULT_HERO_BG = "#0b1f3a";
const DEFAULT_TILE_BG = "#f3f4f6";
const DEFAULT_ACCENT = "#e30613";

// TEMP: remove when real company background_color values are configured in DB
const USE_TEMP_PREVIEW_COLORS = true;
const TEMP_PREVIEW_TILE_COLORS = [
  "#dbeafe",
  "#fef3c7",
  "#ede9fe",
  "#fce7f3",
  "#ccfbf1",
  "#ffedd5",
  "#e0e7ff",
  "#d1fae5",
  "#fee2e2",
];
const TEMP_PREVIEW_HERO_COLOR = "#1e3a5f";

export default {
  name: "BusinessWallMobilePreview",
  props: {
    companies: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      accentColor: DEFAULT_ACCENT,
    };
  },
  computed: {
    sortedCompanies() {
      return [...this.companies].sort((a, b) => {
        const aHighlighted = this.isHighlighted(a);
        const bHighlighted = this.isHighlighted(b);
        if (aHighlighted !== bHighlighted) {
          return bHighlighted ? 1 : -1;
        }
        return (Number(a.order) || 0) - (Number(b.order) || 0);
      });
    },
    highlightedCompany() {
      return this.sortedCompanies.find((company) => this.isHighlighted(company)) || null;
    },
    otherCompanies() {
      if (!this.highlightedCompany) {
        return this.sortedCompanies;
      }
      return this.sortedCompanies.filter(
        (company) => company.company_id !== this.highlightedCompany.company_id
      );
    },
    heroBackground() {
      if (!this.highlightedCompany) {
        return DEFAULT_HERO_BG;
      }
      const color = this.highlightedCompany.background_color;
      if (USE_TEMP_PREVIEW_COLORS && this.isBlankTileColor(color)) {
        return TEMP_PREVIEW_HERO_COLOR;
      }
      return color || DEFAULT_HERO_BG;
    },
    showOtherCompaniesTitle() {
      return this.highlightedCompany && this.otherCompanies.length > 0;
    },
  },
  created() {
    this.loadAccentColor();
  },
  methods: {
    isHighlighted(company) {
      return Number(company.is_highlighted) === 1;
    },
    async loadAccentColor() {
      try {
        const response = await General.getAppParams();
        const appParams = response.data.app_params || [];
        const buttonColorParam = appParams.find((p) => p.key === "appButtonColor");
        if (buttonColorParam && buttonColorParam.value) {
          this.accentColor = buttonColorParam.value;
        }
      } catch (error) {
        // keep default accent
      }
    },
    tileBackground(company, index = 0) {
      const color = company.background_color;
      if (USE_TEMP_PREVIEW_COLORS && this.isBlankTileColor(color)) {
        return TEMP_PREVIEW_TILE_COLORS[index % TEMP_PREVIEW_TILE_COLORS.length];
      }
      return color || DEFAULT_TILE_BG;
    },
    isBlankTileColor(color) {
      const normalized = String(color || "").trim().toLowerCase();
      return (
        !normalized ||
        normalized === "#ffffff" ||
        normalized === "#fff" ||
        normalized === "white"
      );
    },
    isLightColor(color) {
      const hex = String(color || "").replace("#", "");
      if (hex.length !== 6) {
        return true;
      }
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.6;
    },
    companyInitials(name) {
      const parts = String(name || "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      if (!parts.length) {
        return "?";
      }
      if (parts.length === 1) {
        return parts[0].slice(0, 2).toUpperCase();
      }
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    },
  },
};
</script>

<template>
  <div class="business-wall-preview">
    <div class="business-wall-preview__header">
      <h4 class="business-wall-preview__title mb-1">
        <i class="uil uil-mobile-android me-2"></i>
        Mobile Preview
      </h4>
      <p class="text-muted mb-0">
        Live preview of how the business wall appears in the app.
      </p>
    </div>

    <div v-if="loading" class="business-wall-preview__loading text-center my-4">
      <b-spinner variant="primary" class="align-middle"></b-spinner>
      <strong class="ms-2">Loading preview...</strong>
    </div>

    <div v-else class="business-wall-preview__phone-wrap">
      <div class="business-wall-preview__phone">
        <div class="business-wall-preview__screen">
          

          <div class="business-wall-preview__content">
            <div
            v-if="highlightedCompany"
            class="business-wall-preview__hero"
            :style="{ backgroundColor: heroBackground }"
          >
            <img
              v-if="highlightedCompany.logo_url"
              :src="highlightedCompany.logo_url"
              :alt="highlightedCompany.name"
              class="business-wall-preview__hero-logo"
            />
            <div
              v-else
              class="business-wall-preview__hero-fallback"
              :style="{ color: isLightColor(heroBackground) ? '#111827' : '#ffffff' }"
            >
              {{ companyInitials(highlightedCompany.name) }}
            </div>
          </div>
            <div
              v-if="showOtherCompaniesTitle"
              class="business-wall-preview__section-title"
            >
              <span
                class="business-wall-preview__slashes"
                :style="{ color: accentColor }"
              >////</span>
              <span class="business-wall-preview__section-label">Other companies</span>
            </div>

            <div
              v-if="!sortedCompanies.length"
              class="business-wall-preview__empty text-muted text-center"
            >
              No companies on the business wall yet.
            </div>

            <div
              v-else
              class="business-wall-preview__grid"
            >
              <div
                v-for="(company, index) in otherCompanies"
                :key="company.company_id"
                class="business-wall-preview__tile"
                :style="{ backgroundColor: tileBackground(company, index) }"
              >
                <div class="business-wall-preview__tile-logo-wrap">
                  <img
                    v-if="company.logo_url"
                    :src="company.logo_url"
                    :alt="company.name"
                    class="business-wall-preview__tile-logo"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.business-wall-preview {
  height: 100%;
}

.business-wall-preview__header {
  margin-bottom: 1rem;
}

.business-wall-preview__title {
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
}

.business-wall-preview__phone-wrap {
  display: flex;
  justify-content: center;
  padding: 0.5rem 0 1rem;
}

.business-wall-preview__phone {
  width: 360px;
  max-width: 100%;
  border-radius: 32px;
  border: 1px solid #e5e7eb;
  padding: 1px;
  background: #e5e6ea;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.18);
}

.business-wall-preview__screen {
  border-radius: 22px;
  overflow: hidden;
  background: #ffffff;
  min-height: 520px;
  max-height: 640px;
  display: flex;
  flex-direction: column;
}

.business-wall-preview__hero {
  min-height: 132px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px 16px;
  border-radius: 20px;
}

.business-wall-preview__hero-logo {
  max-width: 78%;
  max-height: 72px;
  object-fit: contain;
}

.business-wall-preview__hero-fallback {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.business-wall-preview__content {
  flex: 1;
  overflow-y: auto;
  padding: 6px;
}

.business-wall-preview__section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
}

.business-wall-preview__slashes {
  font-weight: 700;
  font-size: 20px;
  letter-spacing: -0.08em;
  line-height: 1;
}

.business-wall-preview__section-label {
  font-size: 24px;
  font-weight: 500;
  color: #111827;
}

.business-wall-preview__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.business-wall-preview__tile {
  border-radius: 12px;
  width: 100%;
  height: 72px;
  min-height: 72px;
  max-height: 72px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
}

.business-wall-preview__tile-logo-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.business-wall-preview__tile-logo {
  max-width: 85%;
  max-height: 56px;
  object-fit: contain;
}

.business-wall-preview__empty {
  padding: 24px 12px;
  font-size: 13px;
}
</style>
